import React, { ReactNode, useEffect, useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { Theme } from "@literati/public-ui-shared-react";
import { SubscriptionPlanCamelCase } from "lib/api/subscription";
import PaymentCheckboxLegalEnrollment from "components/Payment/PaymentCheckboxLegalEnrollment";

import {
  LegalEnrollmentKey as Key,
  LegalEnrollmentSubmit,
  LegalEnrollmentValues,
  LegalEnrollmentValuesPartial,
} from "components/Payment/paymentFormTypes";

export const FORM_ID = "payment-form-legal-enrollment";

export const VALIDATION_SCHEMA_LEGAL_ENROLLMENT = Yup.object().shape({
  [Key.LEGAL_ENROLLMENT]: Yup.boolean().oneOf([true]),
});

export interface PaymentFormLegalEnrollmentProps {
  className?: string;
  errorMessage?: ReactNode;
  hasFieldError?: boolean;
  onValuesChange?: (values: LegalEnrollmentValues) => void;
  plan?: SubscriptionPlanCamelCase;
  values: LegalEnrollmentValues;
  touched?: boolean;
}

export const PaymentFormLegalEnrollment = ({
  className = "",
  errorMessage = "Please agree with the terms to continue.",
  hasFieldError = false,
  onValuesChange,
  plan,
  values,
  touched = false,
}: PaymentFormLegalEnrollmentProps) => {
  useEffect(() => {
    onValuesChange?.(values);
  }, [onValuesChange, values]);

  return (
    <Theme>
      <Form id={FORM_ID}>
        <PaymentCheckboxLegalEnrollment
          className={className}
          plan={plan}
          errorMessage={errorMessage}
          hasFieldError={hasFieldError}
          touched={touched}
        />
      </Form>
    </Theme>
  );
};

export interface PaymentFormLegalEnrollmentWithFormikProps
  extends Omit<PaymentFormLegalEnrollmentProps, "values"> {
  legalEnrollmentInitialValues?: LegalEnrollmentValuesPartial;
  onSubmit?: LegalEnrollmentSubmit;
}

const PaymentFormLegalEnrollmentWithFormik = ({
  onSubmit,
  ...props
}: PaymentFormLegalEnrollmentWithFormikProps) => {
  const [initialValues] = useState({
    legalEnrollment: false,
    ...props?.legalEnrollmentInitialValues,
  });

  const handleSubmit: LegalEnrollmentSubmit = (values, formikHelpers) => {
    onSubmit?.(values, formikHelpers);
  };

  return (
    <Theme>
      <Formik<LegalEnrollmentValues>
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={VALIDATION_SCHEMA_LEGAL_ENROLLMENT}
      >
        {({ errors, touched, values }) => {
          return (
            <PaymentFormLegalEnrollment
              hasFieldError={Boolean(errors?.[Key.LEGAL_ENROLLMENT])}
              touched={touched?.[Key.LEGAL_ENROLLMENT]}
              values={values}
              {...props}
            />
          );
        }}
      </Formik>
    </Theme>
  );
};

export default PaymentFormLegalEnrollmentWithFormik;
